/**
 * Centralized inert management.
 *
 * Multiple components can claim inert ownership simultaneously.
 * A stack determines which claim is active — the topmost claim drives
 * which elements are inert. When released, the previous claim reasserts.
 *
 * Claims may declare a `priority` (default 0). Higher-priority claims are
 * always positioned above lower-priority claims regardless of insertion
 * order, so a top-layer surface (like the cookie banner) can stay active
 * even if another component claims inert later in the same tick.
 */

interface InertClaim {
  id: string;
  element: Element;
  skip?: string;
  priority: number;
}

const stack: InertClaim[] = [];

const managedElements = new Set<Element>();

function isShopifyInjected(sibling: Element): boolean {
  if (typeof sibling.id !== "string") return false;
  if (sibling.id === "PBarNextFrameWrapper") return true;
  return sibling.id.startsWith("shopify-") && !sibling.classList.contains("shopify-section");
}

function matchesSkip(sibling: Element, skip: string | undefined): boolean {
  if (!skip) return false;
  return sibling.matches(skip) || sibling.querySelector(skip) !== null;
}

function recompute(): void {
  for (const element of managedElements) {
    if (element instanceof HTMLElement) element.inert = false;
  }
  managedElements.clear();

  const top = stack.at(-1);
  if (!top) return;

  let node: Element = top.element;

  while (node.parentElement) {
    const parent = node.parentElement;
    for (const sibling of parent.children) {
      if (sibling === node) continue;
      if (sibling instanceof HTMLElement && sibling.inert) continue;
      if (isShopifyInjected(sibling)) continue;
      if (matchesSkip(sibling, top.skip)) continue;
      if (sibling instanceof HTMLElement) {
        sibling.inert = true;
        managedElements.add(sibling);
      }
    }
    if (parent === document.body) break;
    node = parent;
  }
}

interface ClaimInertOptions {
  skip?: string;
  priority?: number;
}

/**
 * Push or re-push an inert claim onto the stack.
 */
export function claimInert(id: string, element: Element, options?: ClaimInertOptions): void {
  const existing = stack.findIndex((c) => c.id === id);
  if (existing !== -1) stack.splice(existing, 1);
  const claim: InertClaim = {
    id,
    element,
    skip: options?.skip,
    priority: options?.priority ?? 0,
  };
  // Insert after all claims with priority <= ours, so higher-priority
  // claims always stay on top regardless of insertion order.
  let insertAt = 0;
  for (let i = 0; i < stack.length; i++) {
    if (stack[i].priority <= claim.priority) insertAt = i + 1;
  }
  stack.splice(insertAt, 0, claim);
  recompute();
}

/**
 * Release an inert claim. The previous claim reasserts automatically.
 */
export function releaseInert(id: string): void {
  const index = stack.findIndex((c) => c.id === id);
  if (index !== -1) stack.splice(index, 1);
  recompute();
}

/**
 * The id of the topmost active inert claim, or null if none.
 */
export function getActiveInertId(): string | null {
  return stack.at(-1)?.id ?? null;
}
